import { z } from 'zod';
/**
 * Onboarding: configurable templates instantiated per organisation as
 * checklists with dependencies, owners, due dates, blockers, evidence and
 * authority-gated waivers. Go-live readiness is COMPUTED — never a flag an
 * operator can set directly; going live is executed through the approval
 * engine (two-person control).
 */
export const ONBOARDING_INSTANCE_STATUSES = ['in_progress', 'live', 'cancelled'];
export const ONBOARDING_STEP_STATUSES = [
    'pending',
    'in_progress',
    'blocked',
    'complete',
    'waived',
];
/** Operator-drivable step transitions (waived only via approved waiver). */
export const STEP_TRANSITIONS = {
    pending: ['in_progress', 'blocked'],
    in_progress: ['complete', 'blocked', 'pending'],
    blocked: ['pending', 'in_progress'],
    complete: ['in_progress'],
    waived: [],
};
export const EvidenceKindSchema = z.enum(['link', 'note']);
export const EvidenceInputSchema = z
    .object({
    kind: EvidenceKindSchema,
    title: z.string().trim().min(3).max(200),
    /** Link URL or note body. Scanned for prohibited content; never clinical. */
    content: z.string().trim().min(3).max(4000),
})
    .strict();
export const EvidenceSchema = EvidenceInputSchema.extend({
    id: z.uuid(),
    stepId: z.uuid(),
    createdBy: z.string().nullable(),
    createdAt: z.iso.datetime(),
});
export const StepUpdateSchema = z
    .object({
    status: z.enum(['pending', 'in_progress', 'blocked', 'complete']).optional(),
    ownerUserId: z.uuid().nullable().optional(),
    dueDate: z.iso.date().nullable().optional(),
    /** Required when moving to blocked. */
    blockerReason: z.string().trim().min(5).max(1000).nullable().optional(),
    expectedVersion: z.number().int().min(1),
})
    .strict();
export const WaiverRequestSchema = z
    .object({
    reason: z.string().trim().min(10).max(1000),
    /** Waivers are time-bound by design. */
    expiresAt: z.iso.datetime(),
})
    .strict();
export const OnboardingStepSchema = z.object({
    id: z.uuid(),
    instanceId: z.uuid(),
    key: z.string(),
    title: z.string(),
    description: z.string().nullable(),
    sortOrder: z.number().int(),
    status: z.enum(ONBOARDING_STEP_STATUSES),
    evidenceRequired: z.boolean(),
    dependsOnKeys: z.array(z.string()),
    ownerUserId: z.uuid().nullable(),
    ownerDisplayName: z.string().nullable(),
    dueDate: z.iso.date().nullable(),
    blockerReason: z.string().nullable(),
    completedAt: z.iso.datetime().nullable(),
    waiver: z
        .object({
        reason: z.string(),
        approvedBy: z.string().nullable(),
        expiresAt: z.iso.datetime(),
        expired: z.boolean(),
    })
        .nullable(),
    evidence: z.array(EvidenceSchema),
    version: z.number().int(),
});
/** Why a step (and therefore go-live) is not ready. Machine-readable. */
export const ReadinessBlockerSchema = z.object({
    stepKey: z.string(),
    stepTitle: z.string(),
    kind: z.enum([
        'incomplete',
        'blocked',
        'missing_evidence',
        'dependency_unsatisfied',
        'waiver_expired',
    ]),
    detail: z.string(),
});
export const OnboardingInstanceSchema = z.object({
    id: z.uuid(),
    organisationId: z.uuid(),
    organisationName: z.string(),
    templateId: z.uuid(),
    templateName: z.string(),
    status: z.enum(ONBOARDING_INSTANCE_STATUSES),
    startedAt: z.iso.datetime(),
    targetGoLiveDate: z.iso.date().nullable(),
    wentLiveAt: z.iso.datetime().nullable(),
    steps: z.array(OnboardingStepSchema),
    readiness: z.object({
        ready: z.boolean(),
        blockers: z.array(ReadinessBlockerSchema),
        completedSteps: z.number().int(),
        totalSteps: z.number().int(),
        computedAt: z.iso.datetime(),
    }),
    version: z.number().int(),
});
export const StartOnboardingSchema = z
    .object({
    templateId: z.uuid(),
    targetGoLiveDate: z.iso.date().optional(),
})
    .strict();
export const OnboardingTemplateSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    description: z.string().nullable(),
    isActive: z.boolean(),
    stepCount: z.number().int(),
});
//# sourceMappingURL=onboarding.js.map